import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { catalogoService } from 'services/usuarioService';
import { matriculaService } from 'services/matriculaService';
import { MatriculaMasivaItem, MatriculaMasivaError, MatriculaMasivaResponse } from 'types/matricula.types';
import { AñoEscolar } from 'types/usuario.types';
import { fmtAnioEscolar } from 'utils/anioEscolar';

// ============================================================
// MatriculaMasivaPage
// ============================================================

const NIVELES = ['INICIAL', 'PRIMARIA', 'SECUNDARIA'];

// ── Columnas aceptadas desde Excel (español e inglés) ─────────
const COL_MAP: Record<string, keyof MatriculaMasivaItem> = {
  dni: 'dni', dnie: 'dni',
  gradonombredni: 'gradoNombre',
  gradonombre: 'gradoNombre', grado: 'gradoNombre',
  nivel: 'nivel',
  seccionNombre: 'seccionNombre', seccion: 'seccionNombre', sección: 'seccionNombre',
};

const normalizeKey = (k: string) =>
  k.toLowerCase().replace(/[^a-záéíóúñ]/g, '');

const mapColHeader = (raw: string): keyof MatriculaMasivaItem | null => {
  const n = normalizeKey(raw);
  return (COL_MAP[n] as keyof MatriculaMasivaItem) ?? null;
};

const EMPTY_ROW = (): MatriculaMasivaItem => ({ dni: '', gradoNombre: '', nivel: '', seccionNombre: '' });

// ── Componente de campo con error inline ─────────────────────
const Cell = ({
  value, onChange, error, type = 'text', options,
}: {
  value: string; onChange: (v: string) => void; error?: boolean;
  type?: string; options?: string[];
}) => {
  const base = `w-full px-2 py-1.5 text-sm rounded-lg border outline-none transition-colors
    ${error ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500'}`;

  if (options) {
    return (
      <select value={value} onChange={e => onChange(e.target.value)} className={base}>
        <option value="">—</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return (
    <input type={type} value={value} onChange={e => onChange(e.target.value)} className={base}/>
  );
};

// ── KPI card ──────────────────────────────────────────────────
const Kpi = ({ label, value, color }: { label: string; value: number; color: string }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-5 py-4 text-center">
    <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">{label}</p>
    <p className={`text-3xl font-bold mt-1 tabular-nums ${color}`}>{value}</p>
  </div>
);

// ════════════════════════════════════════════════════════════════
const MatriculaMasivaPage: React.FC = () => {
  const fileRef = useRef<HTMLInputElement>(null);

  const [años,     setAños]     = useState<AñoEscolar[]>([]);
  const [anioId,   setAnioId]   = useState('');
  const [cargando, setCargando] = useState(false);

  const [filas,      setFilas]      = useState<MatriculaMasivaItem[]>([EMPTY_ROW()]);
  const [erroresFila, setErroresFila] = useState<Set<number>>(new Set());

  const [enviando,  setEnviando]  = useState(false);
  const [resultado, setResultado] = useState<MatriculaMasivaResponse | null>(null);
  const [errorGlobal, setErrorGlobal] = useState('');

  // Cargar años escolares al montar
  React.useEffect(() => {
    setCargando(true);
    catalogoService.getAñosEscolares()
      .then(data => {
        setAños(data);
        if (data.length) setAnioId(String(data[0].idAnioEscolar));
      })
      .catch(() => {})
      .finally(() => setCargando(false));
  }, []);

  const anioSeleccionado = años.find(a => String(a.idAnioEscolar) === anioId);

  // ── Editar celda ───────────────────────────────────────────
  const setCell = useCallback((idx: number, field: keyof MatriculaMasivaItem, val: string) => {
    setFilas(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));
    setErroresFila(prev => { const s = new Set(prev); s.delete(idx); return s; });
  }, []);

  const addRow    = () => setFilas(prev => [...prev, EMPTY_ROW()]);
  const removeRow = (idx: number) => setFilas(prev => prev.filter((_, i) => i !== idx));

  // ── Leer Excel ────────────────────────────────────────────
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const wb  = XLSX.read(ev.target?.result, { type: 'array' });
      const ws  = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { defval: '' });
      if (!raw.length) return;

      const headers = Object.keys(raw[0]);
      const headerMap: Partial<Record<string, keyof MatriculaMasivaItem>> = {};
      headers.forEach(h => {
        const mapped = mapColHeader(h);
        if (mapped) headerMap[h] = mapped;
      });

      const items: MatriculaMasivaItem[] = raw.map(row => {
        const item = EMPTY_ROW();
        Object.entries(headerMap).forEach(([col, field]) => {
          if (field) (item as any)[field] = String(row[col] ?? '').trim();
        });
        return item;
      }).filter(r => r.dni || r.gradoNombre);

      setFilas(items.length ? items : [EMPTY_ROW()]);
      setResultado(null);
      setErroresFila(new Set());
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  // ── Descargar plantilla ────────────────────────────────────
  const descargarPlantilla = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ['dni',       'gradoNombre', 'nivel',      'seccionNombre'],
      ['74521890',  '1°',          'SECUNDARIA', 'A'],
      ['65432187',  '2°',          'PRIMARIA',   'B'],
    ]);
    ws['!cols'] = [{ wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Matriculas');
    XLSX.writeFile(wb, 'plantilla_matricula_masiva.xlsx');
  };

  // ── Validar y enviar ───────────────────────────────────────
  const validar = (): boolean => {
    const errores = new Set<number>();
    filas.forEach((f, i) => {
      if (!f.dni.trim() || !f.gradoNombre.trim() || !f.nivel.trim() || !f.seccionNombre.trim()) {
        errores.add(i);
      }
    });
    setErroresFila(errores);
    return errores.size === 0;
  };

  const enviar = async () => {
    if (!validar()) return;
    if (!anioId) { setErrorGlobal('Selecciona el año escolar.'); return; }
    setEnviando(true); setErrorGlobal(''); setResultado(null);
    try {
      const res = await matriculaService.registrarMasivo({
        anio: anioSeleccionado!.anio,
        matriculas: filas.map(f => ({
          dni:          f.dni.trim(),
          gradoNombre:  f.gradoNombre.trim(),
          nivel:        f.nivel.trim(),
          seccionNombre: f.seccionNombre.trim(),
        })),
      });
      setResultado(res);
      if (res.totalErrores === 0) setFilas([EMPTY_ROW()]);
    } catch (e: any) {
      setErrorGlobal(e.message ?? 'Error al procesar la solicitud.');
    } finally {
      setEnviando(false);
    }
  };

  // ── Exportar errores a Excel ───────────────────────────────
  const exportarErrores = (errores: MatriculaMasivaError[]) => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ['Fila', 'DNI', 'Motivo del error'],
      ...errores.map(e => [e.fila, e.dni, e.motivo]),
    ]);
    ws['!cols'] = [{ wch: 6 }, { wch: 12 }, { wch: 60 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Errores');
    XLSX.writeFile(wb, `errores_matricula_masiva.xlsx`);
  };

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div>
        <h1 className="text-xl font-bold text-slate-800">Matrícula Masiva</h1>
        <p className="text-slate-400 text-sm mt-0.5">
          Registra múltiples matrículas cargando un archivo Excel o completando la tabla.
        </p>
      </div>

      {/* Panel año escolar + acciones */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Año escolar */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Año escolar</label>
            {cargando ? (
              <div className="px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-400 w-40">Cargando…</div>
            ) : (
              <select value={anioId} onChange={e => setAnioId(e.target.value)}
                className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-700
                           outline-none focus:ring-2 focus:ring-blue-500">
                {años.map(a => (
                  <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>
                ))}
              </select>
            )}
          </div>

          <div className="flex-1"/>

          {/* Botones de acción */}
          <div className="flex flex-wrap gap-2">
            <button onClick={descargarPlantilla}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200
                         bg-white text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-emerald-600">
                <path fillRule="evenodd" d="M3 17a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1Zm3.293-7.707a1 1 0 0 1 1.414 0L9 10.586V3a1 1 0 1 1 2 0v7.586l1.293-1.293a1 1 0 1 1 1.414 1.414l-3 3a1 1 0 0 1-1.414 0l-3-3a1 1 0 0 1 0-1.414Z" clipRule="evenodd"/>
              </svg>
              Plantilla Excel
            </button>

            <button onClick={() => fileRef.current?.click()}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-blue-200
                         bg-blue-50 text-sm font-semibold text-blue-700 hover:bg-blue-100 transition-colors">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M3 3a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7.414A2 2 0 0 0 15.414 6L12 2.586A2 2 0 0 0 10.586 2H4a1 1 0 0 0-1 1Zm5 6a1 1 0 0 1 2 0v3.586l.293-.293a1 1 0 0 1 1.414 1.414l-2 2a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414l.293.293V9Z" clipRule="evenodd"/>
              </svg>
              Cargar Excel
            </button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFile} className="hidden"/>
          </div>
        </div>

        {/* Indicaciones columnas */}
        <p className="text-xs text-slate-400 mt-3">
          Columnas esperadas en el Excel:
          <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded mx-1">dni</span>
          <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded mx-1">gradoNombre</span>
          <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded mx-1">nivel</span>
          <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded mx-1">seccionNombre</span>
        </p>
      </div>

      {/* Tabla de datos */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <p className="text-sm font-bold text-slate-700">
            {filas.length} fila{filas.length !== 1 ? 's' : ''}
          </p>
          <button onClick={addRow}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100
                       hover:bg-slate-200 text-xs font-semibold text-slate-600 transition-colors">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
              <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z"/>
            </svg>
            Añadir fila
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="w-10 px-3 py-2.5 text-xs font-bold text-slate-400 text-center">#</th>
                <th className="px-3 py-2.5 text-xs font-bold text-slate-400 uppercase tracking-wide text-left">DNI</th>
                <th className="px-3 py-2.5 text-xs font-bold text-slate-400 uppercase tracking-wide text-left">Grado</th>
                <th className="px-3 py-2.5 text-xs font-bold text-slate-400 uppercase tracking-wide text-left">Nivel</th>
                <th className="px-3 py-2.5 text-xs font-bold text-slate-400 uppercase tracking-wide text-left">Sección</th>
                <th className="w-10"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filas.map((fila, idx) => {
                const err = erroresFila.has(idx);
                return (
                  <tr key={idx} className={`${err ? 'bg-red-50/40' : 'hover:bg-slate-50'}`}>
                    <td className="px-3 py-2 text-center text-xs text-slate-400 font-mono">{idx + 1}</td>
                    <td className="px-2 py-2 min-w-[110px]">
                      <Cell value={fila.dni} onChange={v => setCell(idx, 'dni', v)} error={err && !fila.dni.trim()}/>
                    </td>
                    <td className="px-2 py-2 min-w-[110px]">
                      <Cell value={fila.gradoNombre} onChange={v => setCell(idx, 'gradoNombre', v)} error={err && !fila.gradoNombre.trim()}/>
                    </td>
                    <td className="px-2 py-2 min-w-[140px]">
                      <Cell value={fila.nivel} onChange={v => setCell(idx, 'nivel', v)}
                        error={err && !fila.nivel.trim()} options={NIVELES}/>
                    </td>
                    <td className="px-2 py-2 min-w-[90px]">
                      <Cell value={fila.seccionNombre} onChange={v => setCell(idx, 'seccionNombre', v)} error={err && !fila.seccionNombre.trim()}/>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button onClick={() => removeRow(idx)} disabled={filas.length === 1}
                        className="text-slate-300 hover:text-red-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                          <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z" clipRule="evenodd"/>
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {erroresFila.size > 0 && (
          <div className="px-5 py-3 border-t border-red-100 bg-red-50 text-xs text-red-600 font-semibold">
            Completa los campos resaltados antes de enviar.
          </div>
        )}
      </div>

      {/* Botón enviar */}
      {errorGlobal && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">
          {errorGlobal}
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={enviar} disabled={enviando || !anioId || filas.length === 0}
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-700 hover:bg-blue-800
                     text-white text-sm font-bold transition-colors shadow-sm
                     disabled:opacity-60 disabled:cursor-not-allowed">
          {enviando ? (
            <>
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
              </svg>
              Procesando…
            </>
          ) : (
            <>
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path d="M3.105 2.289a.75.75 0 0 0-.826.95l1.414 4.925A1.5 1.5 0 0 0 5.135 9.25h6.115a.75.75 0 0 1 0 1.5H5.135a1.5 1.5 0 0 0-1.442 1.086l-1.414 4.926a.75.75 0 0 0 .826.95 28.896 28.896 0 0 0 15.293-7.154.75.75 0 0 0 0-1.115A28.897 28.897 0 0 0 3.105 2.289Z"/>
              </svg>
              Registrar {filas.length} matrícula{filas.length !== 1 ? 's' : ''}
            </>
          )}
        </button>
      </div>

      {/* Panel de resultados */}
      {resultado && (
        <div className="space-y-4">
          <h2 className="font-bold text-slate-800 text-base">Resultado del procesamiento</h2>

          {/* KPIs */}
          <div className="grid grid-cols-3 gap-4">
            <Kpi label="Enviados"  value={resultado.totalEnviados}  color="text-slate-700"/>
            <Kpi label="Creados"   value={resultado.totalCreados}   color="text-green-600"/>
            <Kpi label="Errores"   value={resultado.totalErrores}   color={resultado.totalErrores > 0 ? 'text-red-600' : 'text-slate-400'}/>
          </div>

          {/* Banner de éxito */}
          {resultado.totalErrores === 0 && (
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-2xl px-5 py-4">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-green-600 flex-shrink-0">
                <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd"/>
              </svg>
              <p className="text-sm font-semibold text-green-700">
                ¡Todas las matrículas se registraron correctamente!
              </p>
            </div>
          )}

          {/* Tabla de errores */}
          {resultado.errores.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-red-50">
                <p className="text-sm font-bold text-red-700">
                  {resultado.errores.length} error{resultado.errores.length !== 1 ? 'es' : ''} detectado{resultado.errores.length !== 1 ? 's' : ''}
                </p>
                <button onClick={() => exportarErrores(resultado.errores)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-red-200
                             text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                    <path fillRule="evenodd" d="M3 17a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1Zm3.293-7.707a1 1 0 0 1 1.414 0L9 10.586V3a1 1 0 1 1 2 0v7.586l1.293-1.293a1 1 0 1 1 1.414 1.414l-3 3a1 1 0 0 1-1.414 0l-3-3a1 1 0 0 1 0-1.414Z" clipRule="evenodd"/>
                  </svg>
                  Exportar errores
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase w-16">Fila</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase w-32">DNI</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Motivo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {resultado.errores.map((e, i) => (
                      <tr key={i} className="hover:bg-red-50/30">
                        <td className="px-4 py-3 font-mono text-slate-500">{e.fila}</td>
                        <td className="px-4 py-3 font-mono font-semibold text-slate-700">{e.dni}</td>
                        <td className="px-4 py-3 text-red-600">{e.motivo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Creados */}
          {resultado.creados.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-green-50">
                <p className="text-sm font-bold text-green-700">
                  {resultado.creados.length} matrícula{resultado.creados.length !== 1 ? 's' : ''} registrada{resultado.creados.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Estudiante</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">DNI</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Grado</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Sección</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {resultado.creados.map(m => (
                      <tr key={m.idMatricula} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-semibold text-slate-800">
                          {m.estudianteApellidos}, {m.estudianteNombres}
                        </td>
                        <td className="px-4 py-3 font-mono text-slate-500">{m.estudianteDni}</td>
                        <td className="px-4 py-3 text-slate-600">{m.gradoNombre}</td>
                        <td className="px-4 py-3 text-slate-600">Sección {m.seccionNombre}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MatriculaMasivaPage;
